import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import {
  Aviso,
  Board,
  BoardColuna,
  Contexto,
  Humano,
  Nota,
  Operadora,
  Tag,
  Trilhas,
  WorkCard,
} from '../../../shared/ui/primitivas';
import { CasoDialog } from './DialogoDoCaso';
import { VisaoEmQuadro } from '../VisaoEmQuadro';
import { useChegadaDaCaptacao } from './useChegadaDaCaptacao';
import { useDadosDaCaptacao } from './useDadosDaCaptacao';
import { useCaptacaoSimulacao } from './useEsteiraDaCaptacao';
import {
  MOMENTO_ESTADO,
  ORDEM_DAS_TRILHAS,
  ROTULO_CURTO_DA_TRILHA,
  ROTULO_DA_TRILHA,
  MOSTRAR_PROTOTIPO,
} from './dadosDaCaptacao';
import { variaveisDaMarca } from '../../../tema/variaveisDaMarca';

export function PaginaDeCaptacao() {
  const navigate = useNavigate();
  const { data } = useDadosDaCaptacao();
  // As colunas vêm da simulação, não do mock direto: os casos andam sozinhos e
  // param onde precisam de gente.
  const {
    colunas,
    chegou,
    resolver,
    parados,
    cobrados,
    foraDoPrazo,
    piscando,
    ativo,
    receberCasoReal,
    reais,
    incertos,
  } = useCaptacaoSimulacao();
  // Os casos que a triagem leu de verdade entram na mesma esteira (TP-025).
  const { erro: erroDoDadoReal } = useChegadaDaCaptacao(receberCasoReal);
  // O clique abre o detalhe do caso; só cai na navegação quem não tem detalhe.
  //
  // Guarda o TÍTULO, não o card: com o card, o diálogo ficava com o retrato
  // do instante em que foi aberto, e fechar uma frente não mudava nada na
  // tela. Como o caso tem cinco frentes e quem abre costuma resolver mais de
  // uma, isso obrigava a reabrir o caso a cada gesto.
  const [tituloAberto, setTituloAberto] = useState<string | null>(null);
  const aberto =
    colunas.flatMap((col) => col.cards).find((c) => c.titulo === tituloAberto) ?? null;

  return (
    <div className="anery-marca" style={variaveisDaMarca}>
      {/* Chips e varredura parcial contam números FABRICADOS. Com o protótipo
          desligado eles sairiam mentindo sobre o dado real que está no quadro.
          Antes do board, não depois: a contagem parcial tem que ser lida antes
          dos cards, senão o aviso chega quando a conclusão já foi tirada. */}
      {MOSTRAR_PROTOTIPO && <Contexto chips={data.chips} />}
      {MOSTRAR_PROTOTIPO && data.varreduraParcial && <Aviso>{data.varreduraParcial}</Aviso>}

      {/* Sem isto, a falha pareceria "não chegou nenhum e-mail". A esteira do
          protótipo segue andando: o real não derruba o mock. */}
      {erroDoDadoReal && (
        <Aviso tone="warn">
          Dado real indisponível: os e-mails lidos pela triagem não estão chegando. {erroDoDadoReal.message}
          {erroDoDadoReal.codigo && ` Código: ${erroDoDadoReal.codigo}.`}
          {erroDoDadoReal.correlationId && ` correlation_id: ${erroDoDadoReal.correlationId}.`}
        </Aviso>
      )}

      {MOSTRAR_PROTOTIPO && ativo && (
        <p className="mt-4 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          a fila anda sozinha · {parados} parado{parados === 1 ? '' : 's'} esperando uma pessoa
          {cobrados > 0 && ` · ${cobrados} cobrado${cobrados === 1 ? '' : 's'}`}
          {foraDoPrazo > 0 && ` · ${foraDoPrazo} fora do prazo`}
        </p>
      )}

      {/* O quadro de CASO, que é o que a NR-29 pede: pipeline somente leitura
          por substatus, em colunas, lendo `GET /casos?recorte=captacao`.
          Antes era uma visão de /casos; mora aqui porque é esta a tela da
          captação, e dois quadros do mesmo pipeline era a duplicação. */}
      <VisaoEmQuadro />

      {/* Abaixo do quadro: o que CHEGOU e ainda não virou Caso.
          
          Com o protótipo ligado, são as cinco colunas da simulação. Com ele
          desligado, é UMA coluna — porque o ticket real nunca sai de "Novo",
          e quatro colunas vazias ao lado de um quadro que já tem as suas
          seria o mesmo desenho duas vezes na mesma tela.
          
          Estes tickets importam: são os que entraram na captação e ainda não
          viraram Caso. O que segura a promoção é o nome do paciente e a
          operadora; CPF, hospital e endereço viram pendência do Caso, não
          bloqueio. Sumir com eles esconderia onde o trabalho trava. */}
      <Board>
        {(MOSTRAR_PROTOTIPO ? colunas : colunas.slice(0, 1)).map((col) => (
          <BoardColuna
            key={col.nome}
            nome={MOSTRAR_PROTOTIPO ? col.nome : 'Chegou, ainda não é Caso'}
            contagem={col.contagem}
          >
            {col.cards.map((c) => {
              // O estado do card vem do mesmo lugar que o do detalhe: se a
              // etiqueta do quadro e a do caso discordarem, ninguém confia nas
              // duas.
              const est = c.detalhe?.momento ? MOMENTO_ESTADO[c.detalhe.momento.tipo] : null;
              return (
              <WorkCard
                key={c.titulo}
                titulo={c.titulo}
                sub={c.sub}
                meta={c.meta}
                tone={c.tone}
                flag={est ? { label: est.rotulo, tone: est.tone } : undefined}
                pendente={est?.pessoa ? est.tone : undefined}
                substatus={c.substatus}
                piscando={piscando.has(c.titulo)}
                chegou={chegou.has(c.titulo)}
                onClick={
                  c.detalhe
                    ? () => { setTituloAberto(c.titulo); }
                    : c.irPara
                      ? () => { void navigate(`/${c.irPara ?? ''}`); }
                      : undefined
                }
              >
                {/* A fila é de todas as operadoras; o piloto é só o Bradesco. */}
                {c.operadora && <Operadora nome={c.operadora} />}
                {/* Só faz sentido quando há protótipo do lado para distinguir. */}
                {MOSTRAR_PROTOTIPO && reais.has(c.titulo) && <Tag label="e-mail real" tone="info" />}
                {/* TP-028: o portão deixou passar sem certeza (fail-open, D3). */}
                {incertos.has(c.titulo) && <Tag label="classificação incerta" tone="warn" />}
                {/* Em apuração a coluna diz a fase e a tira diz a frente. */}
                {c.trilhas && (
                  <Trilhas
                    trilhas={c.trilhas}
                    trava={c.trava}
                    ordem={ORDEM_DAS_TRILHAS}
                    rotulos={ROTULO_CURTO_DA_TRILHA}
                    rotulosLongos={ROTULO_DA_TRILHA}
                  />
                )}
              </WorkCard>
              );
            })}
          </BoardColuna>
        ))}
      </Board>

      <CasoDialog
        key={tituloAberto}
        card={aberto}
        onOpenChange={(v) => { if (!v) setTituloAberto(null); }}
        onResolver={resolver}
      />

      <Nota titulo="Por que importa.">
        Hoje o SIAI cai numa caixa, alguém responde "estamos dando andamento" e o IW só registra
        quando a case abriu o processo — não quando a operadora mandou. Sem isso não existe SLA
        medido por etapa. A proposta continua saindo com <Humano /> no clique.
      </Nota>

      <p className="mt-6 font-mono text-[10px] uppercase tracking-wide text-tinta-3">
        protótipo · os cards marcados "e-mail real" vieram da triagem e ficam onde estão até o
        próximo passo; os demais são simulados e não persistem entre visitas
      </p>

      <Toaster position="bottom-right" />
    </div>
  );
}
